// New-Request draft autosave (Story #4613, Stream 8a). Per-user localStorage
// draft with a tabId + updatedAt guard so a stale background tab never clobbers
// a newer draft written by the tab the user is actually typing in. Restored
// drafts carry attachment metadata only (filename/size/type) — never file bytes —
// so the chips render as filename/icon placeholders on restore.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Priority, UiRequestType } from '../types/support'

export interface DraftAttachmentMeta {
  filename: string
  sizeBytes: number
  contentType: string
  isVideo: boolean
}

export interface SupportDraft {
  requestType: UiRequestType
  priority: Priority
  subject: string
  description: string
  includeDiagnostics: boolean
  attachments: DraftAttachmentMeta[]
}

interface StoredDraft extends SupportDraft {
  tabId: string
  updatedAt: number
}

function keyFor(userId: string | undefined): string {
  return `iflow_support_draft_${userId || 'anon'}`
}

function randomTabId(): string {
  return `tab-${Math.random().toString(36).slice(2, 10)}`
}

export interface UseSupportDraft {
  /** The draft loaded at mount, or null when there was none. */
  initialDraft: SupportDraft | null
  save: (draft: SupportDraft) => void
  clear: () => void
}

export function useSupportDraft(userId: string | undefined): UseSupportDraft {
  const tabIdRef = useRef<string>(randomTabId())
  const key = keyFor(userId)
  // Tracks the updatedAt of the newest draft THIS tab knows about, so the guard
  // can tell "another tab wrote something newer than what I've seen" from
  // "I'm the newest".
  const lastSeenRef = useRef<number>(0)

  const [initialDraft] = useState<SupportDraft | null>(() => {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw) as StoredDraft
      lastSeenRef.current = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0
      const { tabId: _tabId, updatedAt: _updatedAt, ...rest } = parsed
      return { ...rest, attachments: rest.attachments ?? [] } as SupportDraft
    } catch {
      return null
    }
  })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const save = useCallback(
    (draft: SupportDraft) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        try {
          // Guard: if another tab has written a draft newer than anything this tab
          // has seen, don't clobber it.
          const existingRaw = localStorage.getItem(key)
          if (existingRaw) {
            const existing = JSON.parse(existingRaw) as StoredDraft
            if (existing.tabId !== tabIdRef.current && existing.updatedAt > lastSeenRef.current) {
              lastSeenRef.current = existing.updatedAt
              return
            }
          }
          const now = Date.now()
          const toStore: StoredDraft = { ...draft, tabId: tabIdRef.current, updatedAt: now }
          localStorage.setItem(key, JSON.stringify(toStore))
          lastSeenRef.current = now
        } catch {
          /* storage full / disabled — autosave is best-effort */
        }
      }, 600)
    },
    [key],
  )

  const clear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    try {
      localStorage.removeItem(key)
    } catch {
      /* non-fatal */
    }
  }, [key])

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  return { initialDraft, save, clear }
}
